import React, { useEffect, useMemo, useRef, useState } from "react";
import { RunApiError, startRun } from "./runApi";
import { ThemeMode, useTheme } from "./theme";
import { resolveApiBase } from "./config";

interface HeroProps {
  onComplete: (payload: { targetUrl: string; runId?: string }) => void;
  resultsPanel?: React.ReactNode;
  currentTheme: ThemeMode;
  onToggleTheme: () => void;
  serverStatus: "waking" | "ready" | "error";
  serverMessage: string;
  onRetryServer?: () => void;
  simulatedMode?: boolean;
}

export default function Hero({
  onComplete,
  resultsPanel,
  currentTheme,
  onToggleTheme,
  serverStatus,
  serverMessage,
  onRetryServer,
  simulatedMode = false,
}: HeroProps) {
  const [siteUrl, setSiteUrl] = useState("");
  const [status, setStatus] = useState<"idle" | "running" | "done">("idle");
  const [progress, setProgress] = useState("");
  const [error, setError] = useState<React.ReactNode | null>(null);
  const { theme } = useTheme();
  const palette = theme.palette;
  const isDark = currentTheme === "dark";
  const serverReady = serverStatus === "ready" || simulatedMode;
  const simulatedTimers = useRef<number[]>([]);
  const apiBase = useMemo(() => resolveApiBase(), []);
  const footerMessage =
    status === "running" ? progress : progress ? progress : serverMessage;

  const toDisplayTarget = (value: string) => {
    const trimmed = (value || "").trim();
    if (!trimmed) return "";
    return trimmed
      .replace(/^https?:\/\//i, "")
      .replace(/^www\./i, "")
      .split("/")[0];
  };

  const clearSimulatedTimers = () => {
    if (typeof window !== "undefined") {
      simulatedTimers.current.forEach((id) => window.clearTimeout(id));
    }
    simulatedTimers.current = [];
  };

  useEffect(() => {
    return () => {
      clearSimulatedTimers();
    };
  }, []);

  const handleRun = async () => {
    clearSimulatedTimers();
    if (!serverReady) {
      setError(
        "Backend is still waking up. Please wait a moment and try again."
      );
      return;
    }
    const rawInput = siteUrl.trim();
    if (!rawInput) {
      setError("Please enter a site URL to test.");
      return;
    }
    // Client-side guard: only allow homepage (no deep paths)
    try {
      const u = new URL(
        /^https?:\/\//i.test(rawInput) ? rawInput : `https://${rawInput}`
      );
      const path = u.pathname || "/";
      if (path !== "/") {
        const home = `${u.origin}/`;
        setError(
          <span>
            <span>
              Free runs are limited to the homepage. Please use{" "}
              <strong>{home}</strong>.
            </span>
            <br />
            <span>
              For full‑site runs, enhanced coverage, and custom test suites,
              please{" "}
              <a
                href="#waitlist"
                style={{ color: palette.accent, textDecoration: "underline" }}
              >
                register your interest
              </a>{" "}
              and we&rsquo;ll notify you when registrations open.
            </span>
          </span>
        );
        return;
      }
    } catch (_) {
      // fall back to server validation
    }
    const renderHomeOnlyError = (home: string) => (
      <span>
        <span>
          Free runs are limited to the homepage. Please use{" "}
          <strong>{home}</strong>.
        </span>
        <br />
        <span>
          For full-site runs, enhanced coverage, and custom test suites, please{" "}
          <a
            href="#waitlist"
            style={{ color: palette.accent, textDecoration: "underline" }}
          >
            register your interest
          </a>{" "}
          and we&rsquo;ll notify you when registrations open.
        </span>
      </span>
    );

    const formatRateLimitMessage = (retryAfterMs?: number) => {
      if (!retryAfterMs) {
        return "Free-tier limit reached. Please try again shortly.";
      }
      const minutes = Math.floor(retryAfterMs / 60000);
      const seconds = Math.round((retryAfterMs % 60000) / 1000);
      const formatted = minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
      return `Free-tier limit reached. Try again in ${formatted}.`;
    };

    if (simulatedMode) {
      setStatus("running");
      const displayTarget = toDisplayTarget(rawInput);
      setError(null);
      setProgress("Validating target URL...");

      if (typeof window === "undefined") {
        setStatus("done");
        setProgress("");
        onComplete?.({ targetUrl: displayTarget || "LaunchSafely Demo Site" });
        if (displayTarget) {
          setSiteUrl(displayTarget);
        }
        return;
      }

      const simulatedSteps = [
        { delay: 900, message: "Launching simulated Playwright runner..." },
        {
          delay: 1500,
          message: "Capturing screenshots and diagnostic traces...",
        },
        {
          delay: 1500,
          message: "Analyzing checks and synthesizing insights...",
        },
        { delay: 2000, message: "Finalizing report..." },
        { delay: 1000, message: "" },
      ];

      let elapsed = 0;
      simulatedSteps.forEach(({ delay, message }) => {
        elapsed += delay;
        const timeoutId = window.setTimeout(() => {
          setProgress(message);
        }, elapsed);
        simulatedTimers.current.push(timeoutId);
      });

      const finalizeId = window.setTimeout(() => {
        setStatus("done");
        setProgress("");
        onComplete?.({ targetUrl: displayTarget || "LaunchSafely Demo Site" });
        if (displayTarget) {
          setSiteUrl(displayTarget);
        }
        simulatedTimers.current = [];
      }, elapsed + 600);
      simulatedTimers.current.push(finalizeId);
      return;
    }
    setStatus("running");
    setProgress("Starting backend run...");
    try {
      const data = await startRun({
        apiBase,
        url: rawInput,
        simulate: simulatedMode,
      });
      const queuePosition =
        typeof data.queuePosition === "number" ? data.queuePosition : null;
      const queueSummary =
        queuePosition && queuePosition > 1
          ? `Queued (position ${queuePosition})...`
          : "Run starting...";

      const displayTarget =
        data.displayHost || toDisplayTarget(data.normalizedUrl) || rawInput;

      setError(null);
      onComplete?.({ targetUrl: displayTarget, runId: data.runId });
      if (displayTarget) {
        setSiteUrl(displayTarget);
      }
      setStatus("running");
      setProgress(queueSummary);
      setTimeout(() => {
        setStatus("done");
        setProgress("");
      }, 1500);
    } catch (err) {
      if (err instanceof RunApiError) {
        if (err.code === "home_only") {
          const home = err.details?.home || "your site homepage";
          setError(renderHomeOnlyError(home));
        } else if (err.code === "rate_limited") {
          setError(formatRateLimitMessage(err.details?.retryAfterMs));
        } else if (err.code === "queue_full") {
          setError("Runner queue is full. Please try again in a moment.");
        } else if (err.code === "invalid_url") {
          setError("Enter a valid URL like https://example.com.");
        } else if (err.code === "invalid_target") {
          setError(
            "That URL points to a network location we cannot scan. Please use a publicly accessible site."
          );
        } else if (err.code === "url_required") {
          setError("Please enter a site URL to test.");
        } else {
          setError(err.message || "Unable to start run. Please try again.");
        }
        setStatus("idle");
        setProgress("");
        return;
      }
      console.warn("Falling back to demo run", err);
      setProgress("Backend unavailable; showing demo report...");
      setTimeout(() => {
        setStatus("done");
        const displayTarget = toDisplayTarget(rawInput);
        setError(null);
        onComplete?.({ targetUrl: displayTarget || "LaunchSafely Demo Site" });
      }, 1200);
    }
  };

  return (
    <header
      style={{
        padding: "3rem 1rem 2rem",
        background: theme.heroBackground,
        color: palette.textPrimary,
      }}
    >
      <div
        style={{
          maxWidth: "960px",
          margin: "0 auto",
          background: palette.card,
          borderRadius: "24px",
          padding: "2.5rem 1rem 1.75rem",
          boxShadow: palette.shadow,
          border: `1px solid ${palette.borderSoft}`,
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            background:
              "radial-gradient(circle at 12% 18%, rgba(101, 192, 221, 0.18), transparent 45%), radial-gradient(circle at 88% 28%, rgba(47, 145, 209, 0.12), transparent 42%)",
          }}
        />
        <button
          onClick={onToggleTheme}
          type="button"
          aria-label={`Switch to ${isDark ? "light" : "dark"} mode`}
          title={`Switch to ${isDark ? "light" : "dark"} mode`}
          style={{
            position: "absolute",
            top: "1.15rem",
            right: "1.15rem",
            width: "3.25rem",
            height: "1.6rem",
            borderRadius: "999px",
            border: `1px solid ${palette.borderSoft}`,
            background: isDark ? palette.cardAlt : palette.card,
            display: "flex",
            alignItems: "center",
            padding: "0.2rem",
            cursor: "pointer",
            boxShadow: "0 10px 20px rgba(15, 23, 42, 0.08)",
            transition: "background 0.3s ease",
          }}
        >
          <span
            style={{
              width: "1.2rem",
              height: "1.2rem",
              borderRadius: "50%",
              background: palette.accentGradient,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transform: isDark ? "translateX(1.55rem)" : "translateX(0)",
              transition: "transform 0.3s ease",
              color: palette.textOnAccent,
              fontSize: "0.8rem",
            }}
          >
            {isDark ? "🌙" : "☀️"}
          </span>
        </button>

        <div style={{ position: "relative", textAlign: "center" }}>
          <h1
            style={{
              fontSize: "2.8rem",
              fontWeight: 800,
              marginBottom: "1.25rem",
              color: palette.textPrimary,
            }}
          >
            Don't launch until you{" "}
            <span
              style={{
                backgroundImage: palette.accentGradient,
                WebkitBackgroundClip: "text",
                color: "transparent",
              }}
            >
              LaunchSafely
            </span>
            .
          </h1>

          <p
            style={{
              fontSize: "1.1rem",
              color: palette.textMuted,
              marginBottom: "2rem",
              lineHeight: 1.7,
            }}
          >
            Instant website test runs for SaaS founders and MVP builders. <br />
            Paste your site URL and get a clear report in seconds. <br />
            <strong style={{ color: palette.textSecondary }}>
              Zero setup. No repo access. Peace of mind.
            </strong>
          </p>

          {resultsPanel && (
            <div
              style={{
                margin: "0 auto 2rem",
                maxWidth: "800px",
                width: "100%",
              }}
            >
              {resultsPanel}
            </div>
          )}

          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleRun();
            }}
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "0.9rem",
              maxWidth: "540px",
              margin: "0 auto 1.75rem",
            }}
          >
            <input
              type="text"
              placeholder="https://your-site.com"
              value={siteUrl}
              onChange={(e) => setSiteUrl(e.target.value)}
              disabled={status === "running"}
              style={{
                flex: 1,
                padding: "0.9rem 1.1rem",
                borderRadius: "12px",
                border: `1px solid ${palette.inputBorder}`,
                background: palette.inputBackground,
                color: palette.textPrimary,
                fontSize: "1rem",
                boxShadow: "0 1px 2px rgba(15, 23, 42, 0.05)",
              }}
            />
            {serverStatus !== "ready" && (
              <div
                style={{
                  color:
                    serverStatus === "error" ? palette.danger : palette.warning,
                  fontSize: "0.9rem",
                  lineHeight: 1.4,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "0.75rem",
                }}
              >
                <span>{serverMessage}</span>
                {serverStatus === "error" && onRetryServer && (
                  <button
                    type="button"
                    onClick={onRetryServer}
                    style={{
                      padding: "0.35rem 0.8rem",
                      borderRadius: "999px",
                      border: `1px solid ${palette.borderSoft}`,
                      background: palette.cardAlt,
                      color: palette.textPrimary,
                      cursor: "pointer",
                      fontSize: "0.8rem",
                      fontWeight: 600,
                    }}
                  >
                    Retry
                  </button>
                )}
              </div>
            )}
            {error && (
              <div
                aria-live="polite"
                style={{
                  color: palette.danger,
                  fontSize: "0.9rem",
                  lineHeight: 1.4,
                }}
              >
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={
                status === "running" || (!serverReady && !simulatedMode)
              }
              style={{
                padding: "0.9rem 1.2rem",
                borderRadius: "12px",
                border: "none",
                fontWeight: 600,
                background: palette.accentGradient,
                color: palette.textOnAccent,
                cursor:
                  status === "running" || !serverReady
                    ? "not-allowed"
                    : "pointer",
                boxShadow: "0 16px 30px rgba(47, 145, 209, 0.25)",
                transition: "transform 0.2s ease",
                opacity: status === "running" || !serverReady ? 0.7 : 1,
              }}
            >
              {status === "running"
                ? "Running..."
                : serverReady
                ? "Run Test"
                : serverStatus === "waking"
                ? "Waking up..."
                : "Retry Backend"}
            </button>
          </form>

          <div
            style={{
              display: "flex",
              gap: "1rem",
              justifyContent: "center",
              flexWrap: "wrap",
              marginBottom: "0.75rem",
            }}
          >
            <a
              href="#waitlist"
              style={{
                display: "inline-block",
                padding: "0.7rem 1.2rem",
                borderRadius: "10px",
                fontWeight: 700,
                textDecoration: "none",
                background: palette.accentGradient,
                color: palette.textOnAccent,
                boxShadow: "0 12px 24px rgba(47, 145, 209, 0.25)",
              }}
            >
              Get Started
            </a>
            <a
              href="#pricing"
              style={{
                display: "inline-block",
                padding: "0.7rem 1.2rem",
                borderRadius: "10px",
                fontWeight: 700,
                textDecoration: "none",
                border: `1px solid ${palette.border}`,
                color: palette.textPrimary,
                background: palette.cardAlt,
              }}
            >
              See Pricing
            </a>
          </div>

          <small style={{ color: palette.textMuted }}>{footerMessage}</small>
        </div>
      </div>
    </header>
  );
}
